import React, {Component} from 'react';
import {inject, observer} from 'mobx-react';
import {injectIntl} from 'react-intl';
import mapValues from 'lodash/mapValues';
import pick from 'lodash/pick';

import Field from '../Field';

//helpers
import {bindField} from '../../utils/input-utils';

import {
  ResetPasswordForm,
  Title,
  StyledDiv,
  StyledButton,
  StyledText,
  StyledIcon,
  StyledErrorWrapper,
  StyledErrorIcon,
  ErrorNotification,
  getHeight,
  StyledButtonLink
} from './styles';

//messages
import messages from './messages';

//data
import {graphql} from '@apollo/client/react/hoc/graphql';

//mutations
import {ForgotPassword, ResetPassword, SetPassword} from '../../api/password/mutations';

//queries
import {ValidateResetPasswordLink, ValidateSetPasswordLink} from '../../api/password/queries';

//options
import {forgotPasswordOptions, resetPasswordOptions, setPasswordOptions} from '../../api/password/mutation-options';
import {validateResetPasswordLinkOptions, validateSetPasswordLinkOptions} from '../../api/password/query-options';

import {posthogCapture} from '../../utils/posthog-utils';
import {posthogEvents} from '../../posthog-events';

import {emailValidationRegex} from '../../constants';
import PhoneInput from 'components/PhoneInput';
import phone from 'phone';

@inject('store')
@graphql(ForgotPassword, forgotPasswordOptions)
@graphql(ResetPassword, resetPasswordOptions)
@graphql(SetPassword, setPasswordOptions)
@graphql(ValidateResetPasswordLink, validateResetPasswordLinkOptions)
@graphql(ValidateSetPasswordLink, validateSetPasswordLinkOptions)
@observer
class ResetYourPassword extends Component {
  UNSAFE_componentWillMount() {
    const {
      intl: {formatMessage},
      store: {resetPasswordPage},
      requestResetPassword
    } = this.props;

    if (!requestResetPassword) {
      posthogCapture(posthogEvents.attemptResetPassword);
    }

    const translations = mapValues(
      pick(messages, ['canNotSentPasswordResetLink', 'canNotResetPassword', 'passwordReset', 'invalidResetCode']),
      message => formatMessage(message)
    );

    resetPasswordPage.setTranslations(translations);
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    const {data} = nextProps;
    const {isPasswordResetLinkValid, isPasswordSetLinkValid} = data;
    const {store} = this.props;
    const {resetPasswordPage} = store;

    if (isPasswordResetLinkValid === false) {
      resetPasswordPage.resetPasswordLinkNotValid();
    }
    if (isPasswordSetLinkValid === false) {
      resetPasswordPage.resetPasswordLinkNotValid();
    }
  }

  handleSubmit = event => {
    event.preventDefault();
    posthogCapture(posthogEvents.requestResetPasswordLink);

    const {store, forgotPasswordMutation, isPhone} = this.props;
    const {resetPasswordPage: page} = store;
    const {validateEmail, validatePhoneNumber} = page;
    if (isPhone) {
      validatePhoneNumber(forgotPasswordMutation);
    } else {
      validateEmail(forgotPasswordMutation);
    }
  };

  resetPassword = event => {
    event.preventDefault();
    const {store, resetPasswordMutation} = this.props;
    const {resetPasswordPage: page, router, app} = store;
    const {views} = app;
    const {validatePassword} = page;
    validatePassword(resetPasswordMutation, router, store, views, 'resetPassword');
  };

  setPassword = event => {
    event.preventDefault();
    const {store, setPasswordMutation} = this.props;
    const {resetPasswordPage: page, router, app} = store;
    const {views} = app;
    const {validatePassword} = page;
    validatePassword(setPasswordMutation, router, store, views, 'setPassword');
  };

  componentWillUnmount() {
    const {store} = this.props;
    store.resetPasswordPage.reset();
  }

  isButtonDisabled = () => {
    const {store, isPhone} = this.props;
    const {
      resetPasswordPage: {form, loading}
    } = store;
    const {email, phoneNumber} = form.values();

    if (loading) return true;

    if (isPhone) {
      return !phoneNumber || phoneNumber.length < 5 || !phone(phoneNumber).isValid;
    }

    return !email || !emailValidationRegex.test(email);
  };

  render() {
    const {
      intl: {formatMessage},
      requestResetPassword,
      setPassword,
      store,
      isMobile,
      isPhone
    } = this.props;
    const {
      resetPasswordPage: {
        isRequestedReset,
        setRequestReset,
        form,
        isInvalidLink,
        emailIsNotSent,
        loading,
        passwordsMatch,
        isValidNewPassword,
        setIsInputFocused,
        isInputFocused
      },
      app,
      router
    } = store;
    const {views} = app;
    const {email, phoneNumber, resetCode, newPassword, newPasswordConfirm} = form.values();
    const passwordFormIsValid = (isPhone ? resetCode : true) && newPassword && newPasswordConfirm;
    const inputStyles = {Input: getHeight()};
    const isValidEmail = email ? emailValidationRegex.test(email) : true;
    const isValidPhoneNumber = phoneNumber && phoneNumber > 5 ? phone(phoneNumber).isValid : true;
    const isInputValid = isPhone ? isValidPhoneNumber : isValidEmail;
    const emailSignInUrl = window.location.origin + `/sign-in/${isPhone ? 'phone-number' : 'email'}`;

    return (
      <ResetPasswordForm isMobile={isMobile}>
        <Title isMobile={isMobile}>
          {formatMessage(setPassword ? messages.setYourPassword : messages.resetYourPassword)}
        </Title>
        {requestResetPassword && (
          <React.Fragment>
            {!isRequestedReset && (
              <React.Fragment>
                <StyledDiv>
                  {formatMessage(isPhone ? messages.enterYourPhoneNumber : messages.enterYourEmail)}
                </StyledDiv>
                {isPhone ? (
                  <PhoneInput title={formatMessage(messages.phoneNumber)} form={form} field="phoneNumber" hideError />
                ) : (
                  <Field
                    placeholder={formatMessage(messages.email)}
                    field="email"
                    id="email"
                    form={form}
                    styles={inputStyles}
                    hideError
                    onFocus={() => setIsInputFocused(true)}
                    onBlur={() => setIsInputFocused(false)}
                    {...bindField(form, 'email')}
                  />
                )}
                {!isInputValid && !isInputFocused && (
                  <StyledErrorWrapper>
                    <StyledErrorIcon name={'warning'} />
                    <ErrorNotification>
                      {formatMessage(isPhone ? messages.invalidPhoneNumber : messages.invalidEmail)}
                    </ErrorNotification>
                  </StyledErrorWrapper>
                )}
                <StyledButton
                  onClick={this.handleSubmit}
                  type="submit"
                  disabled={this.isButtonDisabled()}
                  loading={loading}
                >
                  {formatMessage(isPhone ? messages.sendResetCode : messages.sendResetLink)}
                </StyledButton>
              </React.Fragment>
            )}
            {isRequestedReset && !emailIsNotSent && (
              <StyledDiv alignText={'center'} margin={'30px'}>
                {formatMessage(messages.sentPasswordResetLink)}
              </StyledDiv>
            )}
          </React.Fragment>
        )}

        {!requestResetPassword && (
          <React.Fragment>
            {!isInvalidLink && (
              <React.Fragment>
                {isPhone && (
                  <Field
                    placeholder={formatMessage(messages.resetCode)}
                    id="resetCode"
                    type="number"
                    field="resetCode"
                    form={form}
                    hideError
                    {...bindField(form, 'resetCode')}
                    styles={{
                      Input: {
                        ...getHeight(),
                        margin: '0 0 5px 0'
                      }
                    }}
                    hideArrows
                  />
                )}

                <Field
                  placeholder={formatMessage(messages.newPassword)}
                  id="newPassword"
                  type="password"
                  form={form}
                  field="newPassword"
                  hideError
                  {...bindField(form, 'newPassword')}
                  withPasswordToggle
                  withPasswordValidation
                  styles={inputStyles}
                  onFocus={() => setIsInputFocused(true)}
                  onBlur={() => setIsInputFocused(false)}
                  isInputFocused={isInputFocused}
                />
                {!isValidNewPassword && (
                  <StyledErrorWrapper>
                    <StyledErrorIcon name={'warning'} />
                    <ErrorNotification>{formatMessage(messages.meetAllRequirements)}</ErrorNotification>
                  </StyledErrorWrapper>
                )}

                <Field
                  placeholder={formatMessage(messages.confirmNewPassword)}
                  id="newPasswordConfirm"
                  type="password"
                  field="newPasswordConfirm"
                  form={form}
                  hideError
                  {...bindField(form, 'newPasswordConfirm')}
                  styles={inputStyles}
                  withPasswordToggle
                />
                {!passwordsMatch && (
                  <StyledErrorWrapper>
                    <StyledErrorIcon name={'warning'} />
                    <ErrorNotification>{formatMessage(messages.passwordsDoNotMatch)}</ErrorNotification>
                  </StyledErrorWrapper>
                )}

                {setPassword && (
                  <StyledButton onClick={this.setPassword} disabled={!passwordFormIsValid}>
                    {formatMessage(messages.setPassword)}
                  </StyledButton>
                )}

                {!setPassword && (
                  <StyledButton onClick={this.resetPassword} disabled={!passwordFormIsValid}>
                    {formatMessage(messages.title)}
                  </StyledButton>
                )}
              </React.Fragment>
            )}
            {isInvalidLink && (
              <React.Fragment>
                <StyledDiv alignText={'center'}>
                  {formatMessage(setPassword ? messages.setLinkExpired : messages.resetLinkExpired)}
                </StyledDiv>
                {!setPassword && (
                  <StyledText>
                    <StyledButtonLink
                      onClick={() => {
                        router.goTo(views.requestResetPassword, {});
                        setRequestReset(false);
                      }}
                    >
                      {formatMessage(messages.requestNewResetLink)}
                    </StyledButtonLink>
                  </StyledText>
                )}
              </React.Fragment>
            )}
          </React.Fragment>
        )}

        <StyledText>
          <StyledButtonLink href={emailSignInUrl}>
            <StyledIcon name={'arrow-left'} />
            {formatMessage(messages.returnToSignIn)}
          </StyledButtonLink>
        </StyledText>
      </ResetPasswordForm>
    );
  }
}
export default injectIntl(ResetYourPassword);
